import { AuthScreen, MfaCodeForm, type MfaChallenge } from '@h2hspark/auth-ui';
import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { AuthBrandMark } from '../auth-brand-mark';

interface MfaChallengeLocationState {
  readonly challenge?: MfaChallenge;
  readonly nextPath?: string;
}

/**
 * Continuation of sign-in when the server answers `mfa_required`. The challenge arrives
 * through router state rather than the URL, so the page can't be reached without one.
 *
 * The chrome is the sign-in screen's (2026-09-12). This screen is the *middle* of a sign-in
 * rather than a separate errand — the member pressed "Log in" a second ago — so a change of
 * ground between the two reads as having been handed to a different site at the one moment the
 * product is asking for a security code. Only the surround changed; the challenge, its
 * cancellation and the `nextPath` it carries are as they were.
 */
export function MemberMfaChallengePage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as MfaChallengeLocationState;

  if (state.challenge === undefined) {
    return <Navigate to="/app/login" replace />;
  }

  const nextPath = state.nextPath ?? '/app';

  return (
    <AuthScreen
      chrome="brand"
      mark={<AuthBrandMark />}
      title="Verify it's you"
      description="One more step to finish signing in."
    >
      <MfaCodeForm
        challenge={state.challenge}
        onSuccess={() => navigate(nextPath, { replace: true })}
        onCancel={() => navigate('/app/login', { replace: true })}
      />
    </AuthScreen>
  );
}
